import { useNavigate } from 'react-router-dom';
import {
  useGetClientContextQuery,
  useGetClientDepartmentsQuery,
  useGetClientPartyQuery,
  useGetClientTeamQuery,
  useGetClientGapsQuery,
} from '../../features/platform/platformApi';

/* ------------------------------------------------------------------ *
 * Client Admin — Setup checklist (prototype `isCHome`).
 * The platform provides the state's hierarchies; the client chooses what
 * to use, sets up their party structure and team, and fills gaps. Each
 * step's status is derived from the data the other client pages already
 * fetch (departments, party, team, gaps) — no dedicated checklist API.
 * ------------------------------------------------------------------ */

interface Step {
  title: string;
  status: string;
  done: boolean;
  sub: string;
  go?: string;
  loading?: boolean;
}

export default function ClientHomePage() {
  const navigate = useNavigate();
  const { data: ctx, isLoading: ctxLoading } = useGetClientContextQuery();
  const { data: departments = [], isLoading: deptsLoading } = useGetClientDepartmentsQuery();
  const { data: party, isLoading: partyLoading } = useGetClientPartyQuery();
  const { data: team = [], isLoading: teamLoading } = useGetClientTeamQuery();
  const { data: gaps = [], isLoading: gapsLoading } = useGetClientGapsQuery();

  const orgName = ctx?.organization_name ?? 'your organisation';
  const stateName = ctx?.state_name ?? 'your state';

  const inUse = departments.filter((d) => d.activated).length;
  const available = departments.length;
  const hasParty = party?.has_hierarchy ?? false;
  const partyLevels = party?.levels?.length ?? 0;
  const members = team.length;
  const mappingGaps = gaps.length;

  const steps: Step[] = [
    {
      title: 'Organisation and boundary',
      status: 'Done',
      done: true,
      sub: `${orgName} on the ${stateName} profile, applied from the boundary.`,
      loading: ctxLoading,
    },
    {
      title: 'Departments',
      status: inUse > 0 ? `${inUse} in use` : 'None in use',
      done: inUse > 0,
      sub: `${available} available from the platform, with their territories and officers`,
      go: '/client/departments',
      loading: deptsLoading,
    },
    {
      title: 'Party hierarchy',
      status: hasParty ? `${partyLevels} level${partyLevels === 1 ? '' : 's'}` : 'Not set up',
      done: hasParty,
      sub: 'Your own structure on ECI booths. Never shared.',
      go: '/client/party',
      loading: partyLoading,
    },
    {
      title: 'Team and access',
      status: `${members} member${members === 1 ? '' : 's'}`,
      done: members > 0,
      sub: 'Who handles which requests, and where',
      go: '/client/team',
      loading: teamLoading,
    },
    {
      title: 'Key people and gaps',
      status: mappingGaps > 0 ? `${mappingGaps} mapping gap${mappingGaps === 1 ? '' : 's'}` : 'No mapping gaps',
      done: mappingGaps === 0,
      sub: 'Fill what the platform doesn’t have yet, and add your persons of interest',
      go: '/client/gaps',
      loading: gapsLoading,
    },
  ];

  return (
    <div className="flex flex-col gap-[18px]">
      <section className="flex flex-col gap-1.5">
        <span className="text-xs font-semibold text-brand">CLIENT SETUP · {orgName.toUpperCase()}</span>
        <h1 className="text-2xl font-semibold leading-9 tracking-[-0.75px] text-ink">Set up your organisation</h1>
        <p className="max-w-[780px] text-sm leading-6 text-ink-secondary">
          The platform already provides {stateName}’s hierarchies, territories, designations and many
          officers. You choose what to use, set up your own party structure and team, and fill the
          gaps that matter to you.
        </p>
      </section>

      {steps.map((s, i) => (
        <section
          key={s.title}
          className="grid grid-cols-[36px_minmax(0,1fr)_auto] items-center gap-3 rounded-xl border border-line/30 bg-white p-5 shadow-card"
        >
          <span className="flex size-8 items-center justify-center rounded-full bg-brand text-sm font-bold text-white">
            {i + 1}
          </span>
          <div className="flex min-w-0 flex-col gap-0.5">
            <div className="flex flex-wrap items-center gap-2">
              <b className="text-[15px] text-ink">{s.title}</b>
              {s.loading ? (
                <span className="inline-block h-5 w-20 animate-pulse rounded-full bg-muted-bg" aria-label="Loading" />
              ) : (
                <span className={['rounded-full px-2.5 py-0.5 text-xs font-semibold', s.done ? 'bg-brand-shade text-brand' : 'bg-[#f6e7c8] text-[#6b4a0a]'].join(' ')}>
                  {s.status}
                </span>
              )}
            </div>
            {s.loading ? (
              <span className="mt-1 inline-block h-3.5 w-64 max-w-full animate-pulse rounded bg-muted-bg" aria-hidden />
            ) : (
              <span className="text-[13px] text-ink-secondary">{s.sub}</span>
            )}
          </div>
          {s.go && (
            <button
              type="button"
              onClick={() => navigate(s.go as string)}
              className="h-9 shrink-0 rounded-lg border border-line bg-white px-3 text-xs font-semibold text-ink transition-colors hover:bg-brand-shade"
            >
              Open
            </button>
          )}
        </section>
      ))}
    </div>
  );
}
